import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiDroplet, FiMaximize, FiX } = FiIcons;

const Hero = () => {
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  
  // Náhledový obrázek (zobrazen na stránce)
  const previewImageUrl = "https://media-manager-c.questera.ai/greta-media/6f193b2d92211423257b0e3c0dceb84ada06e24e969d96db08b6a1796096f07796f46a52af0758ac75a61f230dd98906/images/aW1hZ2UvcG5n/04213fc51043499c78e9ebec861dd765.png";
  // Detailní obrázek (zobrazen po rozkliknutí)
  const detailedImageUrl = "https://media-manager-c.questera.ai/greta-media/6f193b2d92211423257b0e3c0dceb84ada06e24e969d96db08b6a1796096f07796f46a52af0758ac75a61f230dd98906/images/aW1hZ2UvcG5n/bb3e9a0cbd611b7a752e63ae9bcea4b0.png";

  return (
    <section className="relative min-h-screen bg-white dark:bg-dark-bg pt-20 flex items-center border-b border-arch-border dark:border-dark-border overflow-hidden transition-colors duration-300">
      {/* Architectural Grid Background - Adapts to Theme */}
      <div 
        className="absolute inset-0 z-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05] bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] dark:bg-[linear-gradient(#fff_1px,transparent_1px),linear-gradient(90deg,#fff_1px,transparent_1px)]" 
        style={{backgroundSize: '40px 40px'}} 
      />
      
      {/* Subtle Colorful Glows for "Barevnost" */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-brand-500/10 dark:bg-brand-500/20 blur-3xl rounded-full pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-accent-teal/10 dark:bg-accent-teal/20 blur-3xl rounded-full pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Content */}
          <motion.div 
            initial={{opacity: 0, y: 20}} 
            animate={{opacity: 1, y: 0}} 
            transition={{duration: 0.8}}
          >
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-12 h-[2px] bg-brand-500"></div>
              <span className="text-xs font-bold text-arch-gray dark:text-gray-400 uppercase tracking-widest">
                Prémiové WPC Obklady
              </span>
            </div>
            
            <h1 className="text-6xl lg:text-7xl font-bold text-arch-dark dark:text-white mb-8 leading-[1.1] tracking-tight">
              Inovace v<br />
              <span className="text-brand-500">každém detailu.</span>
            </h1>
            
            <p className="text-xl text-arch-gray dark:text-gray-400 mb-12 leading-relaxed max-w-lg font-light">
              Revoluční WPC panely s koextrudovanou krystalickou vrstvou a bambusovým uhlíkovým jádrem. Navrženo pro náročné architektonické projekty.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <a 
                href="#contact" 
                className="bg-brand-500 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-brand-600 transition-colors text-center shadow-[0_0_20px_rgba(245,176,135,0.3)] hover:shadow-[0_0_30px_rgba(245,176,135,0.5)]"
              >
                Konzultace projektu
              </a>
              <a 
                href="#product" 
                className="border border-arch-border dark:border-dark-border text-arch-dark dark:text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:border-arch-dark dark:hover:border-white transition-colors text-center bg-white/50 dark:bg-dark-surface/50 backdrop-blur-sm"
              >
                Technická data
              </a>
            </div>

            {/* Quick Specs - Colorful Icons */}
            <div className="grid grid-cols-3 gap-8 py-8 border-t border-arch-border dark:border-dark-border">
              <div>
                <SafeIcon icon={FiShield} className="w-5 h-5 text-accent-teal mb-3" />
                <div className="text-sm font-bold text-arch-dark dark:text-white uppercase tracking-wider">Třída B1</div>
                <div className="text-xs text-arch-gray dark:text-gray-400 mt-1">Požární odolnost</div>
              </div>
              <div>
                <SafeIcon icon={FiDroplet} className="w-5 h-5 text-accent-sage mb-3" />
                <div className="text-sm font-bold text-arch-dark dark:text-white uppercase tracking-wider">100%</div>
                <div className="text-xs text-arch-gray dark:text-gray-400 mt-1">Voděodolnost</div>
              </div>
              <div>
                <SafeIcon icon={FiMaximize} className="w-5 h-5 text-brand-500 mb-3" />
                <div className="text-sm font-bold text-arch-dark dark:text-white uppercase tracking-wider">2800 mm</div>
                <div className="text-xs text-arch-gray dark:text-gray-400 mt-1">Výška panelu</div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Pure Image */}
          <motion.div 
            className="relative hidden lg:block cursor-pointer group"
            initial={{opacity: 0}} 
            animate={{opacity: 1}} 
            transition={{duration: 1, delay: 0.3}}
            onClick={() => setIsImageExpanded(true)}
          >
            {/* Zcela čistý kontejner bez okrajů a pozadí */}
            <div className="relative w-full overflow-hidden rounded-lg shadow-2xl">
              <img 
                src={previewImageUrl} 
                alt="7 vrstev dokonalosti stěnového panelu WALL WELL - Náhled" 
                className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 bg-white text-arch-dark text-xs font-bold px-6 py-3 uppercase tracking-widest transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0 shadow-xl rounded-sm">
                  Zvětšit detail
                </span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Fullscreen Image Modal */}
      <AnimatePresence>
        {isImageExpanded && (
          <motion.div 
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 sm:p-8 backdrop-blur-md"
            onClick={() => setIsImageExpanded(false)}
          >
            <button 
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors p-3 bg-black/40 hover:bg-black/80 rounded-full z-50"
              onClick={() => setIsImageExpanded(false)}
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
            
            <motion.div 
              className="relative w-full max-w-5xl max-h-[90vh] flex items-center justify-center"
              initial={{scale: 0.95, y: 20}}
              animate={{scale: 1, y: 0}}
              exit={{scale: 0.95, y: 20}}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Detailní obrázek bez okrajů */}
              <img 
                src={detailedImageUrl} 
                alt="7 vrstev dokonalosti stěnového panelu WALL WELL - Detailní struktura" 
                className="max-w-full max-h-[90vh] object-contain pointer-events-none select-none rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Hero;